import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu } from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';

const navLinks = [
  { label: 'Work', href: '/work' },
  { label: 'Services', href: '/services' },
  { label: 'Rentals', href: '/rentals' },
  { label: 'Shoot in Mexico', href: '/mexico' },
  { label: 'Journal', href: '/journal' },
  { label: 'Director', href: '/director' },
  { label: 'Contact', href: '/contact' },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location]);

  return (
    <nav
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-[hsl(222,18%,6%)]/90 backdrop-blur-md border-b border-[hsl(var(--bk-line))]'
          : 'bg-black/30 backdrop-blur-sm'
      }`}
    >
      <div className="max-w-[1200px] mx-auto px-5 sm:px-8 flex items-center justify-between h-16 md:h-20">
        {/* Logo */}
        <Link to="/" className="flex items-center hover:opacity-80 transition-opacity">
          <img 
            src="/assets/baklight-logo-white.png" 
            alt="Baklight" 
            className="h-7 md:h-8 w-auto"
          />
        </Link>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              className={`font-body text-sm tracking-wide transition-colors duration-200 ${
                location.pathname === link.href
                  ? 'text-[hsl(var(--bk-fg))]'
                  : 'text-[hsl(var(--bk-fg-muted))] hover:text-[hsl(var(--bk-fg))]'
              }`}
            >
              {link.label}
            </Link>
          ))}
          <Link
            to="/contact"
            data-testid="nav-contact-cta"
            className="font-body text-sm px-5 py-2 bg-[hsl(var(--bk-fg))] text-[hsl(var(--bk-bg))] rounded-[var(--radius)] hover:opacity-90 transition-opacity active:scale-[0.98]"
          >
            Start a Project
          </Link>
        </div>

        {/* Mobile Nav */}
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <button
              data-testid="site-nav-mobile-menu-button"
              className="md:hidden text-[hsl(var(--bk-fg))] p-2"
            >
              <Menu size={24} />
            </button>
          </SheetTrigger>
          <SheetContent side="right" className="bg-[hsl(var(--bk-bg))] border-l border-[hsl(var(--bk-line))] w-[300px]">
            <div className="flex flex-col gap-6 mt-12">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`font-display text-2xl transition-colors duration-200 ${
                    location.pathname === link.href
                      ? 'text-[hsl(var(--bk-fg))]'
                      : 'text-[hsl(var(--bk-fg-muted))] hover:text-[hsl(var(--bk-fg))]'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <Link
                to="/contact"
                className="font-body text-sm px-5 py-3 bg-[hsl(var(--bk-fg))] text-[hsl(var(--bk-bg))] rounded-[var(--radius)] text-center hover:opacity-90 transition-opacity mt-4"
              >
                Start a Project
              </Link>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </nav>
  );
};
